import React, { useState, useMemo, useCallback } from 'react';
import {
  Box, Heading, Container, Input, InputGroup, InputLeftElement,
  Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon,
  Text, Alert, AlertIcon, HStack, Badge, Divider, SimpleGrid, Card, CardBody,
  VStack, Icon,
} from '@chakra-ui/react';
import { FiSearch, FiBook, FiHeadphones, FiDollarSign, FiSmartphone, FiMonitor, FiBookOpen } from 'react-icons/fi';
import { useUser } from '../Context/UserContext';

// Role-specific manuals
import generalManual from './manuals/generalManual';
import suporteManual from './manuals/suporteManual';
import configManual from './manuals/configManual';
import financeiroManual from './manuals/financeiroManual';
import tiManual from './manuals/tiManual';

// Map roles to their manual groups
const ROLE_MANUALS = {
  Suporte: { label: 'Suporte', colorScheme: 'blue', sections: suporteManual },
  Financeiro: { label: 'Finanças', colorScheme: 'green', sections: financeiroManual },
  'Administração': { label: 'Finanças', colorScheme: 'purple', sections: financeiroManual },
  Marketing: { label: 'Config. Mobile App', colorScheme: 'pink', sections: configManual },
  Design: { label: 'Config. Mobile App', colorScheme: 'cyan', sections: configManual },
  TI: { label: 'Tecnologia', colorScheme: 'teal', sections: tiManual },
};

// Quick-nav card definitions for Master
const QUICK_NAV_CARDS = [
  {
    label: 'Suporte',
    icon: FiHeadphones,
    colorScheme: 'blue',
    topics: 'Contratados, Contratantes, Cancelamentos, Ajuda, Chat Admin',
    sectionId: 'section-suporte',
  },
  {
    label: 'Finanças',
    icon: FiDollarSign,
    colorScheme: 'green',
    topics: 'Pagamentos, Assinaturas, Fluxo de Caixa, Contas',
    sectionId: 'section-financas',
  },
  {
    label: 'Config. Mobile App',
    icon: FiSmartphone,
    colorScheme: 'pink',
    topics: 'Categorias, Subcategorias, Cores do App',
    sectionId: 'section-config-mobile-app',
  },
  {
    label: 'Tecnologia',
    icon: FiMonitor,
    colorScheme: 'teal',
    topics: 'Usuários Admin, Permissões',
    sectionId: 'section-tecnologia',
  },
  {
    label: 'Geral',
    icon: FiBookOpen,
    colorScheme: 'gray',
    topics: 'Login, Navegação, Dashboard, Perfil',
    sectionId: 'section-geral',
  },
];

// Create a stable section ID from a group label
const toSectionId = (label) => 'section-' + label.toLowerCase().replace(/[.\s]+/g, '-').replace(/[^a-z0-9-]/g, '');

const HelpPage = () => {
  const { user } = useUser();
  const [search, setSearch] = useState('');
  const [expandedGroup, setExpandedGroup] = useState(null);

  const isMaster = user?.permissions?.includes('Master');

  // Build manual groups based on user permissions
  const manualGroups = useMemo(() => {
    const groups = [];
    const permissions = user?.permissions || [];

    // General manual is always included
    groups.push({ label: 'Geral', colorScheme: 'gray', sections: generalManual });

    if (isMaster) {
      groups.push({ label: 'Suporte', colorScheme: 'blue', sections: suporteManual });
      groups.push({ label: 'Finanças', colorScheme: 'green', sections: financeiroManual });
      groups.push({ label: 'Config. Mobile App', colorScheme: 'pink', sections: configManual });
      groups.push({ label: 'Tecnologia', colorScheme: 'teal', sections: tiManual });
    } else {
      const added = new Set();
      for (const perm of permissions) {
        const manual = ROLE_MANUALS[perm];
        if (manual && !added.has(manual.label)) {
          groups.push(manual);
          added.add(manual.label);
        }
      }
    }

    return groups;
  }, [user, isMaster]);

  // Flatten all sections for search, keeping group info
  const allSections = useMemo(() => {
    return manualGroups.flatMap(group =>
      group.sections.map(section => ({ ...section, groupLabel: group.label, groupColor: group.colorScheme }))
    );
  }, [manualGroups]);

  // Filter by search
  const filteredSections = useMemo(() => {
    if (!search.trim()) return allSections;
    const term = search.toLowerCase();
    return allSections.filter(
      s => s.title.toLowerCase().includes(term) || s.keywords.toLowerCase().includes(term)
    );
  }, [search, allSections]);

  // Group filtered sections by their group label for display
  const groupedFiltered = useMemo(() => {
    const groups = [];
    let currentGroup = null;

    for (const section of filteredSections) {
      if (!currentGroup || currentGroup.label !== section.groupLabel) {
        currentGroup = { label: section.groupLabel, colorScheme: section.groupColor, sections: [] };
        groups.push(currentGroup);
      }
      currentGroup.sections.push(section);
    }

    return groups;
  }, [filteredSections]);

  // Handle quick-nav card click
  const handleCardClick = useCallback((card) => {
    setExpandedGroup(card.label);
    // Scroll after a brief delay to let the accordion expand
    setTimeout(() => {
      const el = document.getElementById(card.sectionId);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 100);
  }, []);

  const isSearching = search.trim().length > 0;

  return (
    <Container maxW="container.lg" py={8}>
      <HStack mb={2}>
        <Box as={FiBook} size="24px" color="teal.500" />
        <Heading size="lg">Ajuda e FAQ</Heading>
      </HStack>
      <Text color="gray.500" mb={6}>
        Manual do Painel Administrativo TOVI
        {user?.permissions && !isMaster && (
          <> — {user.permissions.map(p => ROLE_MANUALS[p]?.label || p).filter((v, i, a) => a.indexOf(v) === i).join(', ')}</>
        )}
        {isMaster && <> — Todas as áreas</>}
      </Text>

      <InputGroup mb={6} maxW="500px">
        <InputLeftElement pointerEvents="none">
          <FiSearch color="gray" />
        </InputLeftElement>
        <Input
          placeholder="Buscar no manual..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setExpandedGroup(null); }}
        />
      </InputGroup>

      {/* Quick-Nav Cards — Master only, hidden during search */}
      {isMaster && !isSearching && (
        <Box mb={8}>
          <Text fontWeight="semibold" color="gray.600" mb={3} fontSize="sm" textTransform="uppercase" letterSpacing="wide">
            Acesso Rápido
          </Text>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacing={4}>
            {QUICK_NAV_CARDS.map((card) => {
              const sectionCount = manualGroups.find(g => g.label === card.label)?.sections.length || 0;
              return (
                <Card
                  key={card.label}
                  cursor="pointer"
                  onClick={() => handleCardClick(card)}
                  borderLeft="4px solid"
                  borderLeftColor={`${card.colorScheme}.400`}
                  transition="all 0.2s"
                  _hover={{
                    shadow: 'lg',
                    borderLeftColor: `${card.colorScheme}.600`,
                    transform: 'translateY(-2px)',
                  }}
                >
                  <CardBody py={4} px={5}>
                    <HStack spacing={3} mb={2}>
                      <Icon as={card.icon} boxSize={5} color={`${card.colorScheme}.500`} />
                      <Heading size="sm">{card.label}</Heading>
                      <Badge colorScheme={card.colorScheme} fontSize="xs" ml="auto">{sectionCount}</Badge>
                    </HStack>
                    <Text fontSize="xs" color="gray.500">{card.topics}</Text>
                  </CardBody>
                </Card>
              );
            })}
          </SimpleGrid>
        </Box>
      )}

      {/* Manual Sections */}
      {filteredSections.length === 0 ? (
        <Alert status="info" borderRadius="md">
          <AlertIcon />
          Nenhum resultado encontrado para "{search}".
        </Alert>
      ) : (
        groupedFiltered.map((group, gi) => {
          const sectionId = toSectionId(group.label);
          const isExpanded = expandedGroup === group.label || isSearching;

          return (
            <Box key={gi} mb={6} id={sectionId}>
              <HStack mb={3}>
                <Badge colorScheme={group.colorScheme} fontSize="sm" px={3} py={1} borderRadius="md">
                  {group.label}
                </Badge>
                {gi > 0 && <Divider flex={1} />}
              </HStack>

              <Accordion
                allowMultiple
                defaultIndex={isExpanded ? group.sections.map((_, i) => i) : gi === 0 && !isMaster ? [0] : []}
                key={`${group.label}-${isExpanded}`}
              >
                {group.sections.map((section, idx) => (
                  <AccordionItem key={idx} border="1px solid" borderColor="gray.200" borderRadius="md" mb={3}>
                    <AccordionButton py={3} _expanded={{ bg: `${group.colorScheme}.50` }}>
                      <Box flex="1" textAlign="left" fontWeight="semibold" fontSize="md">
                        {section.title}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel pb={4} pt={3}>
                      {section.content}
                    </AccordionPanel>
                  </AccordionItem>
                ))}
              </Accordion>
            </Box>
          );
        })
      )}

      <Text fontSize="sm" color="gray.400" mt={8} textAlign="center">
        Manual TOVI - Painel Administrativo — Última atualização: Setembro 2026
      </Text>
    </Container>
  );
};

export default HelpPage;
